import { useFormikContext } from 'formik';
import { RootState } from 'models';
import { DocumentFieldsSectionField, FieldGroup } from 'models/response';
import { FormValues, Q1Value } from 'models/documents';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { useUpdateEffect } from 'react-use';
import services from 'services';
import selectors from 'state/selectors';
import utils from 'utils';
import { actionType } from 'utils/constants';
import _ from 'lodash';
import actions from 'state/actions';
import CommonButton from 'components/Buttons/CommonButton';
import useCheckBeforeAction from '../hooks';

let previousdocumentFlatFields: DocumentFieldsSectionField[] = [];
let flagApplyRules = false;

interface Props {
  isValidate: boolean;
  disabled: boolean;
}

const ApplyAndValidate = ({ isValidate, disabled }: Props) => {
  const documentFlatFields = useSelector(selectors.documents.selectDocumentFlatFields);
  const protocol = useSelector(selectors.documents.selectActiveDocumentID);
  const protocols = useSelector(selectors.documents.selectAllActiveDocumentsID);
  const documentHeader = useSelector(selectors.documents.selectDocumentHeader);
  const history = useHistory();
  const idUserQualityCheck = useSelector(selectors.user.getIdUser);
  const idProgram = useSelector(selectors.app.getProgramCode);
  const records = useSelector(selectors.documents.selectAllActiveDocuments);
  const textTranslate = utils.app.textTranslated;
  const dispatch = useDispatch();
  const t = utils.intl.useTranslator();
  const autosave =
    useSelector((store: RootState) => selectors.app.getConfig(store, actionType.AUTOSAVE_DOCUMENT))?.val === 1;
  const { setStatus, submitCount } = useFormikContext<{ [key: string]: Q1Value | FormValues }>() || {};
  const saveDocument = utils.hooks.useSaveDocument(true);
  const { checkDocuments } = useCheckBeforeAction(actionType.APPLY_VAL_LOGICS);

  const getQ3Values = (q3Object: FieldGroup | null) => {
    if (q3Object !== null) {
      const formattedQ3Value: { [key: number]: any[] } = {};

      formattedQ3Value[q3Object.idFieldGroup] = q3Object.tableValues.map((rowObj) => {
        const result: { [key: string]: string } = {};

        for (const [key, value] of Object.entries(rowObj)) {
          result[key] = value.content;
        }
        return result;
      });
      return formattedQ3Value;
    } else {
      return null;
    }
  };

  const applyRules = async (documentFields?: DocumentFieldsSectionField[]) => {
    const fields = (documentFields ?? documentFlatFields).map((el) => ({
      // q1 field id
      idField: el.idField,
      // q1 values
      fieldValues: el.values.map((el) => el.content),
      // q3 values
      accountFields: getQ3Values(el.fieldGroup),
      num: el.position,
    }));

    try {
      if (protocol && documentHeader?.actualDocumentType?.idDocType) {
        const { data } = await services.applyRules({
          protocol,
          idDocType: documentHeader.actualDocumentType.idDocType,
          supplierCode: documentHeader.supplierCode ?? '',
          // @ts-ignore
          fields,
        });

        const status = data.fields.map((el: any) => ({ idField: el.idField, status: el.fieldStatus }));
        setStatus(status);
        if (data.docStatus === 1) {
          utils.app.notify(
            'fail',
            textTranslate(data.docMessageStatus, t) || t('error-submitting-the-document'),
            10000,
          );
        } else if (data.docStatus === 2 && data.docMessageStatus) {
          utils.app.notify('warning', textTranslate(data.docMessageStatus, t), 10000);
        }
        return data;
      }
    } catch (e) {
      console.error(e);
    }
  };

  const checkAndSubmit = async (documentFields?: DocumentFieldsSectionField[]) => {
    try {
      const data = await applyRules(documentFields);
      if (data) {
        if (data.docStatus !== 1 && protocol && idUserQualityCheck) {
          await services.validateDocument({ idProgram, idUserQualityCheck, protocols, forceSubmit: false });
          dispatch(actions.documents.removeRows([protocol]));
          utils.app.notify('success', t('success-validation'), 5000);
          dispatch(actions.documents.openNextDocument(history));
        }
      }
    } catch (e) {
      console.error(e);
    }
    flagApplyRules = false;
  };

  useUpdateEffect(() => {
    submitCount > 0 && autosave && flagApplyRules && checkAndSubmit();
  }, [!_.isEqual(documentFlatFields, previousdocumentFlatFields)]);

  React.useEffect(() => {
    previousdocumentFlatFields = documentFlatFields;
  }, [documentFlatFields]);

  const handleValidation = async () => {
    const docFree = await checkDocuments(actionType.VALIDATE);
    if (!docFree) return;
    const val = await saveDocument();
    if (val) {
      await checkAndSubmit(
        val.reduce((acc: DocumentFieldsSectionField[], ele: any) => [...acc, ...ele.sectionFields], []),
      );
      return;
    }
  };

  const handleApplyLogics = async () => {
    const docFree = await checkDocuments();
    if (!docFree) return;
    const val = await saveDocument();
    if (val) {
      applyRules(val.reduce((acc: DocumentFieldsSectionField[], ele) => [...acc, ...ele.sectionFields], []));
      return;
    }
  };

  return isValidate ? (
    <CommonButton
      id={actionType.VALIDATE}
      action={handleValidation}
      scope="tertiary"
      value={t('validate')}
      disabled={disabled}
      customIcon="double-check"
    />
  ) : (
    <CommonButton
      id={actionType.APPLY_VAL_LOGICS}
      action={handleApplyLogics}
      scope="tertiary"
      value={t('apply-val-logics')}
      customIcon="cycle"
      disabled={!utils.user.isActionEnabled(records, actionType.APPLY_VAL_LOGICS)}
    />
  );
};

export default ApplyAndValidate;
